import React, { HTMLAttributes, MouseEventHandler } from 'react';
import styled from 'styled-components';
import { boxShadows } from '@99designs/emu/settings';
import { IconTick2 } from '@99designs/icons';

export interface SelectableBoxProps extends HTMLAttributes<HTMLButtonElement> {
    selected?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
}

const SelectableBoxContainer = styled.button<SelectableBoxProps>`
    border: none;
    padding: 0;
    display: block;
    background: white;
    transition-property: box-shadow;
    transition-duration: 0.3s;
    transition-timing-function: ease-out;
    color: initial;
    position: relative;
    width: 100%;
    height: 100%;
    text-align: left;

    box-shadow: ${({ disabled }) => (disabled ? 'none' : boxShadows.low)};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
    user-select: text;

    ${({ selected }) =>
        selected &&
        `
        &:before {
            content: '';
            position: absolute;
            top: 0;
            bottom: -1px;
            left: 0;
            right: 0;
            border: solid 5px currentColor;
            z-index: 1;
            pointer-events: none;
        }
    `}

    ${({ disabled }) =>
        !disabled &&
        `
        :hover, :focus-visible{
            transition-property: box-shadow;
            transition-duration: 0.1s;
            transition-timing-function: ease-in;
            box-shadow: 0 14px 42px #0003;
            text-decoration: none;
            cursor: pointer;
            outline: none;
        }
    `}
`;

const SelectableBoxCheck = styled.div`
    display: block;
    position: absolute;
    background-image: none;
    color: currentColor;
    height: auto;
    width: auto;
    bottom: 10px;
    right: 10px;
    font-size: 24px;
    line-height: 1;
`;

function SelectableBox({ onClick, disabled, selected, children, ...rest }: SelectableBoxProps) {
    const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
        if (disabled) {
            return;
        }

        if (onClick) {
            onClick(e);
        }
    };

    return (
        <SelectableBoxContainer
            type="button"
            disabled={disabled}
            selected={selected}
            data-disabled={disabled}
            data-selected={selected}
            onClick={handleClick}
            {...rest}
        >
            {children}
            {selected && (
                <SelectableBoxCheck>
                    <IconTick2 />
                </SelectableBoxCheck>
            )}
        </SelectableBoxContainer>
    );
}

export { SelectableBox };
